import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500">漫画未找到</p>
            <Link to="/" className="text-primary-500 text-sm mt-2 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01T00:00:00+08:00',
    dateModified: `${comic.updateTime}T00:00:00+08:00`,
    url: `http://dakunkj.cn/article/${slug}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
    image: comic.coverImage,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEOHead />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.category, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <article className="mt-8">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-shrink-0 w-full lg:w-[300px]">
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - ${comic.category}韩漫`}
                      title={`${comic.title} 韩漫在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                        comic.status === '连载中'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400"></i>
                      <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">({comic.ratingCount.toLocaleString()} 人评价)</span>
                    </div>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">{comic.author}</span>
                    <span className="text-foreground-300">|</span>
                    <Link
                      to={`/category/${comic.categorySlug}`}
                      className="text-sm text-primary-500 bg-primary-50 px-2.5 py-0.5 rounded-full hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap"
                    >
                      {comic.category}
                    </Link>
                  </div>

                  <div className="flex flex-wrap gap-4 mt-6">
                    <div className="bg-background-100 border border-background-200/70 rounded-lg px-4 py-2.5">
                      <p className="text-xs text-foreground-500">总话数</p>
                      <p className="text-sm font-semibold text-foreground-900 mt-0.5">{comic.chapters} 话</p>
                    </div>
                    <div className="bg-background-100 border border-background-200/70 rounded-lg px-4 py-2.5">
                      <p className="text-xs text-foreground-500">阅读量</p>
                      <p className="text-sm font-semibold text-foreground-900 mt-0.5">{comic.views.toLocaleString()}</p>
                    </div>
                    <div className="bg-background-100 border border-background-200/70 rounded-lg px-4 py-2.5">
                      <p className="text-xs text-foreground-500">最近更新</p>
                      <p className="text-sm font-semibold text-foreground-900 mt-0.5">{comic.updateTime}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-foreground-600 bg-background-100 border border-background-200/70 px-3 py-1 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 flex items-center justify-center gap-2">
                      <i className="ri-play-circle-line"></i>
                      开始阅读（下拉式）
                    </button>
                    <button className="border border-background-300 text-foreground-700 hover:bg-background-100 px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                      <i className="ri-heart-line"></i>
                      收藏漫画
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">作品简介</h2>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {comic.description}
                  </p>
                </div>
              </div>
            </article>

            {relatedComics.length > 0 && (
              <section className="mt-16">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} {...c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}